import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdSearch, MdLocationOn, MdBusiness, MdLayers, MdMeetingRoom, MdAdd, MdVisibility, MdEdit, MdDelete, MdToggleOn, MdToggleOff } from 'react-icons/md';
import PageHeader from '../components/PageHeader';
import StatusToggle from '../../../Components/StatusToggle';
import AddRoomModal from '../components/AddRoomModal';
import { conferenceRoomApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './BookRoom.css';

function ConferenceRooms() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({ query: '', location: '', tower: '', floor: '' });
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [error, setError] = useState('');

  const openCreate = () => {
    setShowModal(true);
  };

  const openView = (row) => {
    navigate(`/admin/community/conference-rooms/${row.id}`);
  };

  const openEdit = (row) => {
    navigate(`/admin/community/conference-rooms/${row.id}`);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  useEffect(() => {
    void loadList();
  }, []);

  const loadList = async () => {
    try {
      setIsTableLoading(true);
      setError('');
      const data = await conferenceRoomApi.getAll();
      const arr = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : Array.isArray(data?.content) ? data.content : [];
      setList(arr);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to fetch workspaces');
    } finally {
      setIsTableLoading(false);
    }
  };

  // Get unique values for filters
  const uniqueLocations = useMemo(() => {
    const locations = [...new Set(list.map(room => room.location).filter(Boolean))];
    return locations.sort();
  }, [list]);

  const uniqueTowers = useMemo(() => {
    let towersToShow = list;
    if (filters.location) {
      towersToShow = list.filter(room => room.location === filters.location);
    }
    const towers = [...new Set(towersToShow.map(room => room.tower).filter(Boolean))];
    return towers.sort();
  }, [list, filters.location]);

  const uniqueFloors = useMemo(() => {
    let floorsToShow = list;
    if (filters.location) {
      floorsToShow = floorsToShow.filter(room => room.location === filters.location);
    }
    if (filters.tower) {
      floorsToShow = floorsToShow.filter(room => room.tower === filters.tower);
    }
    const floors = [...new Set(floorsToShow.map(room => room.floor).filter(Boolean))];
    return floors.sort();
  }, [list, filters.location, filters.tower]);

  const filtered = useMemo(() => {
    const q = (filters.query || '').toLowerCase().trim();
    return list.filter((room) => {
      const name = (room.roomName || '').toLowerCase();
      const type = (room.roomType || '').toLowerCase();
      const location = (room.location || '').toLowerCase();
      const tower = (room.tower || '').toLowerCase();
      const floor = (room.floor || '').toLowerCase();

      const matchesSearch = !q || name.includes(q) || type.includes(q) || location.includes(q) || tower.includes(q) || floor.includes(q);
      const matchesLocation = !filters.location || room.location === filters.location;
      const matchesTower = !filters.tower || room.tower === filters.tower;
      const matchesFloor = !filters.floor || room.floor === filters.floor;

      return matchesSearch && matchesLocation && matchesTower && matchesFloor;
    });
  }, [list, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };

      // Reset dependent filters when parent filter changes
      if (field === 'location') {
        newFilters.tower = '';
        newFilters.floor = '';
      } else if (field === 'tower') {
        newFilters.floor = '';
      }

      return newFilters;
    });
  };

  const handleResetFilters = () => {
    setFilters({ query: '', location: '', tower: '', floor: '' });
  };

  const handleStatusToggle = async (row, nextIsActive) => {
    try {
      const newStatus = nextIsActive ? 'ACTIVE' : 'INACTIVE';
      await conferenceRoomApi.updateStatus(row.id, newStatus);
      toast.success('Workspace status updated successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
      await loadList();
    } catch (e) {
      const errorMsg = e.response?.data?.message || e.message || 'Failed to update status';
      setError(errorMsg);
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Are you sure you want to delete "${row.roomName}"?`)) {
      return;
    }

    try {
      await conferenceRoomApi.delete(row.id);
      toast.success('Workspace deleted successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
      await loadList();
    } catch (e) {
      const errorMsg = e.response?.data?.message || e.message || 'Failed to delete workspace';
      setError(errorMsg);
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const getRoomIcon = (roomType) => {
    const icons = {
      'MEETING ROOM': <MdMeetingRoom />,
      'CONFERENCE ROOM': <MdBusiness />,
      'MEDICAL ROOM': <MdMeetingRoom />,
      'BOARD ROOM': <MdBusiness />,
      'TRAINING ROOM': <MdMeetingRoom />,
      'default': <MdMeetingRoom />
    };
    return icons[roomType?.toUpperCase()] || icons.default;
  };

  return (
    <div className="admin-view conference-rooms-view">
      <PageHeader breadcrumbs={["Community", "Workspaces"]} />

      {/* Combined Header and Filters Row */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdMeetingRoom className="title-icon" />
            Manage Workspaces
          </h2>
        </div>

        <div className="header-filters-compact">
          <div className="input search-compact">
            <span className="icon"><MdSearch /></span>
            <input
              placeholder="Search workspaces..."
              value={filters.query}
              onChange={(e) => handleFilterChange('query', e.target.value)}
            />
          </div>

          <select
            value={filters.location}
            onChange={(e) => handleFilterChange('location', e.target.value)}
            className="input select-compact"
          >
            <option value="">Location</option>
            {uniqueLocations.map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>

          <select
            value={filters.tower}
            onChange={(e) => handleFilterChange('tower', e.target.value)}
            className="input select-compact"
            disabled={!filters.location}
          >
            <option value="">Tower</option>
            {uniqueTowers.map(tower => (
              <option key={tower} value={tower}>{tower}</option>
            ))}
          </select>

          <select
            value={filters.floor}
            onChange={(e) => handleFilterChange('floor', e.target.value)}
            className="input select-compact"
            disabled={!filters.tower}
          >
            <option value="">Floor</option>
            {uniqueFloors.map(floor => (
              <option key={floor} value={floor}>{floor}</option>
            ))}
          </select>

          <button onClick={handleResetFilters} className="btn-reset-compact">
            Reset
          </button>

          <button
            className="btn-book-compact"
            onClick={openCreate}
            style={{ marginLeft: '0.5rem' }}
          >
            <MdAdd />
            <span>Add Workspace</span>
          </button>
        </div>
      </div>

      {/* Workspaces List - Modern Card Layout */}
      <div className="rooms-list-container">
        {isTableLoading && (
          <div className="loading-state">
            <div className="loading-spinner-large"></div>
            <p>Loading workspaces...</p>
          </div>
        )}

        {!isTableLoading && filtered.length > 0 && (
          <div className="rooms-list">
            {filtered.map((room) => (
              <div key={room.id} className="room-row">
                {/* Room Icon & Name */}
                <div className="room-main-info">
                  <div className="room-icon-wrapper">
                    <span className="room-icon">{getRoomIcon(room.roomType)}</span>
                  </div>
                  <div className="room-name-section">
                    <div className="room-name-row">
                      <h3 className="room-name">{room.roomName}</h3>
                      <div className="status-toggle-inline">
                        <StatusToggle
                          id={`room-status-${room.id}`}
                          isActive={(room.status || 'ACTIVE') === 'ACTIVE'}
                          onToggle={(next) => handleStatusToggle(room, next)}
                        />
                        <span className="status-label">
                          {room.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <span className="room-type-label">{room.roomType}</span>
                  </div>
                </div>

                {/* Room Details */}
                <div className="room-details-section">
                  <div className="room-detail-item">
                    <span className="detail-icon"><MdLocationOn /></span>
                    <span className="detail-value">{room.location}</span>
                  </div>
                  <div className="room-detail-item">
                    <span className="detail-icon"><MdBusiness /></span>
                    <span className="detail-value">{room.tower}</span>
                  </div>
                  <div className="room-detail-item">
                    <span className="detail-icon"><MdLayers /></span>
                    <span className="detail-value">{room.floor}</span>
                  </div>
                  {room.capacity && (
                    <div className="room-detail-item">
                      <span className="detail-icon">👥</span>
                      <span className="detail-value">{room.capacity} people</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="room-action-section">
                  <button
                    className="btn-action-compact btn-view"
                    onClick={() => openView(room)}
                    title="View details"
                  >
                    <MdVisibility />
                  </button>
                  <button
                    className="btn-action-compact btn-edit"
                    onClick={() => openEdit(room)}
                    title="Edit workspace"
                  >
                    <MdEdit />
                  </button>
                  <button
                    className="btn-action-compact btn-delete"
                    onClick={() => handleDelete(room)}
                    title="Delete workspace"
                  >
                    <MdDelete />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!isTableLoading && filtered.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3>No Workspaces Found</h3>
            <p>
              {filters.query || filters.location || filters.tower || filters.floor
                ? 'Try adjusting your filters to see more results'
                : 'There are currently no workspaces in the system'}
            </p>
            {(filters.query || filters.location || filters.tower || filters.floor) && (
              <button onClick={handleResetFilters} className="empty-action">
                Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>

      <AddRoomModal
        isOpen={showModal}
        onClose={closeModal}
        onSuccess={loadList}
      />
    </div>
  );
}

export default ConferenceRooms;
